import React, { useEffect, useMemo, MouseEventHandler, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "react-i18next";

import { Formik, FormikHelpers } from "formik";
import * as yup from "yup";

import {
  HvButton,
  HvDialog,
  HvDialogTitle,
  HvDialogContent,
  HvDialogActions,
  HvInput,
  HvTextArea,
  HvCheckBox,
  HvBanner,
} from "@hitachivantara/uikit-react-core";

import useStyles from "./styles";

export type SopStepsDialogInitialValues = {
  id: string;
  name: string;
  description: string;
  instructions: string;
};

export type SopStepsDialog = {
  isOpen: boolean;
  onSave: (data: SopStep, addAnother?: boolean) => SopStep | undefined;
  onClose: (success: boolean, message?: string) => void;
  initialValues?: SopStepsDialogInitialValues;
  mode: "add" | "edit";
};

const trimValues = (values: SopStep) => {
  const trimmedValues = Object.entries(values).map(([key, value]) => {
    const isString = typeof value === "string";
    return [key, isString ? (value as string).trim() : value];
  });

  return Object.fromEntries(trimmedValues);
};

const SopAddEditStepsDialog: React.FC<SopStepsDialog> = ({
  isOpen,
  onSave,
  onClose,
  initialValues,
  mode,
}) => {
  const { t } = useTranslation("sopAddEdit");
  const classes = useStyles();
  const [addAnother, setAddAnother] = useState(false);
  const [bannerOpened, setBannerOpened] = useState(false);
  const [bannerStepIndex, setBannerStepIndex] = useState(0);

  const validationSchema = useMemo(() => {
    return yup.object({
      name: yup.string().required(t("validation.required")),
      description: yup.string(),
      instructions: yup.string().required(t("validation.required")),
    });
  }, [t]);

  useEffect(() => {
    if (bannerOpened) {
      const timeout = setTimeout(() => setBannerOpened(false), 5000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [bannerOpened]);

  const onSubmitHandler = async (
    values: SopStep,
    actions: FormikHelpers<SopStep>
  ) => {
    try {
      const step = onSave(trimValues(values), addAnother);

      if (step) {
        setBannerStepIndex(step.order || 0);
        setBannerOpened(true);
      }
      actions.resetForm({
        values: {
          name: "",
          description: "",
          instructions: "",
        },
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (error.message === "name_already_exists") {
        actions.setFieldError("name", t("validation.stepNameAlreadyTaken"));
      }
    }
  };

  const handleClose = () => {
    setAddAnother(false);
    onClose?.(false);
    setBannerOpened(false);
  };

  const handleDialogClose = (
    event: MouseEventHandler<HTMLButtonElement>,
    reason: unknown
  ) => {
    if (reason !== "backdropClick") {
      handleClose();
    }
  };

  const renderBanner = useMemo(
    () => (
      <HvBanner
        className={classes.banner}
        style={{ top: undefined }}
        open={bannerOpened}
        variant="success"
        showIcon
        label={t(`stepsDialog.success`, {
          order: bannerStepIndex + 1,
        })}
        onClose={() => setBannerOpened(false)}
      />
    ),
    [bannerOpened, bannerStepIndex, classes.banner, t]
  );

  return (
    <HvDialog
      buttonTitle={t("buttons.close")}
      classes={{ paper: classes.paper }}
      open={isOpen || addAnother}
      onClose={handleDialogClose}
    >
      <Formik
        initialValues={
          initialValues || {
            name: "",
            description: "",
            instructions: "",
          }
        }
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
      >
        {(props) => {
          const {
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            isValid,
          } = props;

          const parseStatus = (field: keyof SopStep) =>
            errors[field] && touched[field] ? "invalid" : "valid";

          const parseStatusMessage = (field: keyof SopStep) => {
            return errors[field] && touched[field] ? errors[field] : "";
          };

          const handleFieldValue = (fieldName: string, fieldValue: string) => {
            if (bannerOpened) setBannerOpened(false);
            setFieldValue(fieldName, fieldValue);
          };

          return (
            <form onSubmit={handleSubmit} noValidate>
              <HvDialogTitle className={classes.title}>
                {t(`stepsDialog.title.${mode}`)}
              </HvDialogTitle>
              <HvDialogContent>
                {renderBanner}
                <HvInput
                  className={classes.input}
                  inputProps={{
                    autoComplete: "off",
                  }}
                  name="name"
                  required
                  label={t("stepsDialog.fields.name")}
                  placeholder={t("stepsDialog.fields.namePlaceholder")}
                  value={values.name}
                  status={parseStatus("name")}
                  statusMessage={parseStatusMessage("name")}
                  onBlur={() => setFieldTouched("name")}
                  onChange={(evt, value) => handleFieldValue("name", value)}
                />
                <HvTextArea
                  className={clsx(classes.margin)}
                  classes={{ input: classes.textareaInput }}
                  name="description"
                  label={t("stepsDialog.fields.description")}
                  placeholder={t("stepsDialog.fields.descriptionPlaceholder")}
                  rows={3}
                  value={values.description}
                  status={parseStatus("description")}
                  statusMessage={parseStatusMessage("description")}
                  onBlur={() => setFieldTouched("description")}
                  onChange={(evt, value) =>
                    handleFieldValue("description", value)
                  }
                />
                <HvTextArea
                  className={classes.textarea}
                  classes={{ input: classes.textareaInput }}
                  name="instructions"
                  label={t("stepsDialog.fields.instructions")}
                  placeholder={t("stepsDialog.fields.instructionsPlaceholder")}
                  rows={3}
                  value={values.instructions}
                  status={parseStatus("instructions")}
                  statusMessage={parseStatusMessage("instructions")}
                  onBlur={() => setFieldTouched("instructions")}
                  onChange={(evt, value) =>
                    handleFieldValue("instructions", value)
                  }
                  required
                />
              </HvDialogContent>
              <HvDialogActions>
                {mode === "add" && (
                  <HvCheckBox
                    checked={addAnother}
                    onChange={(_evt, checked) => {
                      setAddAnother(checked);
                      setBannerOpened(false);
                    }}
                    label={t("buttons.addAnother")}
                  />
                )}
                <div style={{ flex: 1 }} />
                <HvButton
                  category="ghost"
                  type="submit"
                  disabled={isSubmitting || !isValid || values.name === ""}
                >
                  {t(`buttons.${mode}`)}
                </HvButton>
                <HvButton
                  category="ghost"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  {t("buttons.cancel")}
                </HvButton>
              </HvDialogActions>
            </form>
          );
        }}
      </Formik>
    </HvDialog>
  );
};

export default SopAddEditStepsDialog;
